import { useNavigate } from "react-router-dom";
import { useSignUp, type UserSignup } from "@/api";
import { authErrorMessage, useAuth } from "@/auth";
import { getError } from "@/components/utility/utility/error-utility";
import { ToastHost, useToast } from "@/utility/toast-utility";
import {
  Anchor,
  Box,
  Button,
  Checkbox,
  PasswordInput,
  SimpleGrid,
  Stack,
  Stepper,
  Text,
  TextInput,
  Title,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { EULAContent } from "@/components/documents/eula-content";
import { PrivacyPolicyContent } from "@/components/documents/privacy-policy-content";
import { TermsAndConditionsContent } from "@/components/documents/terms-and-conditions";
import { DocumentModal } from "@/components/documents/document-modal";
import { DateInput } from "@mantine/dates";

import { useFormik } from "formik";
import { useState, useEffect } from "react";

import * as Yup from "yup";


export default function SignUp() {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const { mutateAsync: registerProfile } = useSignUp();
  const auth = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [privacyAccepted, setPrivacyAccepted] = useState<boolean>(false);
  const [eulaAccepted, setEulaAccepted] = useState<boolean>(false);
  const [openEulaModal, setOpenEulaModal] = useState<boolean>(false);
  const [openPrivacyModal, setOpenPrivacyModal] = useState<boolean>(false);
  const [openTermsModal, setOpenTermsModal] = useState<boolean>(false);
  const { closeToast, showToast, toast } = useToast();

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type) {
        switch (event.data.type) {
          case "AGREE_TERMS":
            setTermsAccepted(true);
            break;
          case "AGREE_PRIVACY":
            setPrivacyAccepted(true);
            break;
          case "AGREE_EULA":
            setEulaAccepted(true);
            break;
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const formik = useFormik<UserSignup>({
    initialValues: {
      email: "",
      password: "",
      first_name: "",
      last_name: "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .required("First name is required")
        .matches(
          /^[A-Za-z]+$/,
          "First name must contain only alphabetic characters (A-Z, a-z)"
        )
        .min(1, "First name cannot be empty")
        .max(255, "First name cannot exceed 255 characters")
        .test(
          "no-whitespace",
          "First name cannot contain whitespace",
          (value) => {
            if (!value) return true; // Let required validation handle empty values
            return !/\s/.test(value);
          }
        )
        .test(
          "no-underscore",
          "First name cannot contain underscore",
          (value) => {
            if (!value) return true; // Let required validation handle empty values
            return !value.includes("_");
          }
        )
        .label("First Name"),
      middle_name: Yup.string()
        .matches(
          /^[A-Za-z]*$/,
          "Middle name must contain only alphabetic characters (A-Z, a-z)"
        )
        .test(
          "no-whitespace",
          "Middle name cannot contain whitespace",
          (value) => {
            if (!value) return true; // Allow empty middle name
            return !/\s/.test(value);
          }
        )
        .test(
          "no-underscore",
          "Middle name cannot contain underscore",
          (value) => {
            if (!value) return true; // Allow empty middle name
            return !value.includes("_");
          }
        )
        .max(255, "Middle name cannot exceed 255 characters")
        .label("Middle Name"),
      last_name: Yup.string()
        .required("Last name is required")
        .matches(
          /^[A-Za-z]+$/,
          "Last name must contain only alphabetic characters (A-Z, a-z)"
        )
        .min(1, "Last name cannot be empty")
        .max(255, "Last name cannot exceed 255 characters")
        .test(
          "no-whitespace",
          "Last name cannot contain whitespace",
          (value) => {
            if (!value) return true; // Let required validation handle empty values
            return !/\s/.test(value);
          }
        )
        .test(
          "no-underscore",
          "Last name cannot contain underscore",
          (value) => {
            if (!value) return true; // Let required validation handle empty values
            return !value.includes("_");
          }
        )
        .label("Last Name"),
      date_of_birth: Yup.date()
        .required("Date of birth is required")
        .max(new Date(), "Date of birth cannot be in the future")
        .min(new Date("1900-01-01"), "Date of birth cannot be before 1900")
        .label("Date of Birth"),
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email is required")
        .matches(/^[^\s]+$/, "Email cannot contain spaces")
        .matches(/^[a-zA-Z0-9@._-]+$/, "Email contains unsupported symbols")
        .label("Email"),
      password: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters long")
        .max(64, "Password cannot exceed 64 characters")
        .matches(
          /^(?=.*[A-Z])/,
          "Password must contain at least one uppercase letter"
        )
        .matches(
          /^(?=.*[a-z])/,
          "Password must contain at least one lowercase letter"
        )
        .matches(/^(?=.*[0-9])/, "Password must contain at least one number")
        .matches(
          /^(?=.*[@$!%*?&])/,
          "Password must contain at least one special character (@$!%*?&)"
        )
        .matches(/^\S+$/, "Password cannot contain spaces")
        .label("Password"),
      phone_number: Yup.string()
        .test(
          "no-alphabets",
          "Phone number cannot contain alphabets",
          (value) => {
            if (!value) return true; // Allow empty
            return !/[a-zA-Z]/.test(value);
          }
        )
        .test("no-spaces", "Phone number cannot contain spaces", (value) => {
          if (!value) return true; // Allow empty
          return !/\s/.test(value);
        })
        .test("no-hyphens", "Phone number cannot contain hyphens", (value) => {
          if (!value) return true; // Allow empty
          return !value.includes("-");
        })
        .test(
          "no-special-chars",
          "Phone number cannot contain special characters",
          (value) => {
            if (!value) return true; // Allow empty
            return !/[^0-9+]/.test(value); // Only allow digits and plus sign
          }
        )
        .max(255, "Phone number cannot exceed 255 characters")
        .label("Phone Number"),
      "address.street_address": Yup.string()
        .max(255, "Street address cannot exceed 255 characters")
        .test(
          "not-only-special-chars",
          "Street address cannot contain only special characters",
          (value) => {
            if (!value) return true; // Allow empty
            return !/^[^a-zA-Z0-9\s]+$/.test(value);
          }
        )
        .label("Street Address"),
      "address.city": Yup.string()
        .test("no-numbers", "City cannot contain numbers", (value) => {
          if (!value) return true; // Allow empty
          return !/\d/.test(value);
        })
        .test(
          "no-special-chars",
          "City cannot contain special characters",
          (value) => {
            if (!value) return true; // Allow empty
            return !/[^a-zA-Z\s]/.test(value);
          }
        )
        .label("City"),
      "address.state": Yup.string()
        .matches(/^[A-Za-z\s]+$/, "State must contain only letters")
        .min(2, "State must be at least 2 characters long")
        .max(50, "State cannot exceed 50 characters")
        .label("State"),
      "address.zip_code": Yup.string()
        .matches(
          /^[1-9]\d*$/,
          "Zip code must contain only numeric digits and cannot start with zero"
        )
        .test("length", "Zip code must be 4 to 5 digits", (value) => {
          if (!value) return true; // Allow empty
          return value.length >= 4 && value.length <= 5;
        })
        .test("no-spaces", "Zip code cannot contain spaces", (value) => {
          if (!value) return true; // Allow empty
          return !/\s/.test(value);
        })
        .label("Zip Code"),
      "address.country": Yup.string()
        .matches(
          /^[A-Za-z\s]+$/,
          "Country must contain only alphabetic characters"
        )
        .min(2, "Country must be at least 2 characters long")
        .max(56, "Country cannot exceed 56 characters")
        .label("Country"),
    }),
    onSubmit: async (values, formikHelpers) => {
      // 1. Firebase account (sends the verification email). 2. Profile fields go to
      // the API, which keys them to the signed-in Firebase user. 3. Verify screen.
      try {
        await auth.signUp(values.email.trim(), values.password);
      } catch (err) {
        showToast({ type: "error", message: authErrorMessage(err) });
        return;
      }
      try {
        const { password: _password, ...profile } = values;
        await registerProfile({ data: { ...profile, password: "" } });
      } catch (err) {
        const { message, fieldErrors } = getError(err);
        showToast({ type: "error", message });
        if (fieldErrors) formikHelpers?.setErrors(fieldErrors);
        return;
      }
      navigate("/auth/verify-email", { replace: true });
    },
  });

  return (
    <Box>
      <ToastHost toast={toast} onClose={closeToast} />

      <Title
        ta="center"
        order={3}
        onClick={() => setActiveStep(0)}
        style={{ 
          cursor: "pointer",
          color: colorScheme === "dark" ? "#ffffff" : "#000000",
        }}
      >
        Sign Up
      </Title>

      <form onSubmit={formik.handleSubmit}>
        <Box style={{ position: "relative" }} mt={{ base: "sm", sm: "md" }}>
          <Stepper
            active={activeStep}
            size="sm"
            styles={{
              stepBody: {
                marginTop: 8,
              },
              step: {
                "&[data-completed]": {
                  "& .mantine-Stepper-stepIcon": {
                    backgroundColor: theme.colors.positive[6],
                    borderColor: theme.colors.positive[6],
                    color: "white",
                  },
                },
                "&[data-progress]": {
                  "& .mantine-Stepper-stepIcon": {
                    backgroundColor: theme.colors.positive[6],
                    borderColor: theme.colors.positive[6],
                    color: "white",
                  },
                },
              },
            }}
          >
            <Stepper.Step label="Basic Info" color="positive" />
            <Stepper.Step label="Address Info" color="positive" />
            <Stepper.Completed>Done</Stepper.Completed>
          </Stepper>

          <Box
            onClick={() => {
              setActiveStep(0);
            }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: "50%",
              height: "100%",
              cursor: "pointer",
              zIndex: 10,
            }}
          />

          <Box
            onClick={async () => {
              if (activeStep === 0) {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length === 0) {
                  setActiveStep(1);
                } else {
                  formik.setTouched({
                    email: true,
                    password: true,
                    first_name: true,
                    last_name: true,
                    date_of_birth: true,
                  });
                }
              } else if (activeStep === 1) {
                setActiveStep(1);
              }
            }}
            style={{
              position: "absolute",
              top: 0,
              left: "50%",
              right: 0,
              height: "100%",
              cursor: "pointer",
              zIndex: 10,
            }}
          />
        </Box>

        {activeStep === 0 && (
          <SimpleGrid
            cols={{ base: 1, sm: 2 }}
            mt="md"
            spacing={{ base: "sm", sm: "md" }}
          >
            <TextInput
              withAsterisk
              name="first_name"
              value={formik.values?.first_name ?? ""}
              onChange={formik.handleChange}
              label="First Name"
              placeholder="Enter First Name"
              error={formik.touched?.first_name && formik.errors?.first_name}
              size="md"
              styles={{
                label: {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  marginBottom: 8,
                },
                input: {
                  height: 40,
                  fontSize: "0.9rem",
                },
              }}
            />
            <TextInput
              // withAsterisk
              name="middle_name"
              value={formik.values?.middle_name ?? ""}
              onChange={formik.handleChange}
              label="Middle Name"
              placeholder="Enter Middle Name"
              error={formik.touched?.middle_name && formik.errors?.middle_name}
              size="md"
              styles={{
                label: {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  marginBottom: 8,
                },
                input: {
                  height: 40,
                  fontSize: "0.9rem",
                },
              }}
            />
            <TextInput
              name="last_name"
              withAsterisk
              value={formik.values?.last_name ?? ""}
              onChange={formik.handleChange}
              label="Last Name"
              placeholder="Enter Last Name"
              error={formik.touched?.last_name && formik.errors?.last_name}
              size="md"
              styles={{
                label: {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  marginBottom: 8,
                },
                input: {
                  height: 40,
                  fontSize: "0.9rem",
                },
              }}
            />
            <DateInput
              withAsterisk
              name="date_of_birth"
              label="Date of Birth"
              placeholder="Select Date"
              id="profile-date-of-birth-input"
              key="profile-date-of-birth-input"
              value={formik.values?.date_of_birth ?? null}
              onChange={(value) => {
                formik.setFieldValue("date_of_birth", value);
              }}
              maxDate={new Date()}
              error={
                formik.touched?.date_of_birth && formik.errors?.date_of_birth
              }
              size="md"
              autoComplete="bday"
              // type="date"
              styles={{
                label: {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  marginBottom: 8,
                },
                input: {
                  height: 40,
                  fontSize: "0.9rem",
                },
              }}
            />
            <TextInput
              withAsterisk
              name="email"
              label="Email"
              placeholder="Enter Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && formik.errors.email}
              size="md"
              styles={{
                label: {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  marginBottom: 8,
                },
                input: {
                  height: 40,
                  fontSize: "0.9rem",
                },
              }}
            />
            <PasswordInput
              withAsterisk
              label="Password"
              name="password"
              placeholder="Enter Password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && formik.errors.password}
              size="md"
              styles={{
                label: {
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  marginBottom: 8,
                },
                input: {
                  height: 40,
                  fontSize: "0.9rem",
                  caretColor: colorScheme === "dark" ? "#ffffff" : "#000000",
                },
              }}
            />
          </SimpleGrid>
        )}

        {activeStep === 1 && (
          <>
            <SimpleGrid
              cols={{ base: 1, sm: 2 }}
              mt="md"
              spacing={{ base: "sm", sm: "md" }}
            >
              <TextInput
                // withAsterisk
                name="phone_number"
                label="Phone Number"
                placeholder="Enter Number"
                value={formik.values.phone_number}
                onChange={formik.handleChange}
                error={
                  formik.touched.phone_number && formik.errors.phone_number
                }
                size="md"
                styles={{
                  label: {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    marginBottom: 8,
                  },
                  input: {
                    height: 40,
                    fontSize: "0.9rem",
                  },
                }}
              />
              <TextInput
                // withAsterisk
                name="address.street_address"
                label="Street Address"
                placeholder="Enter Street Address"
                value={formik.values.address?.street_address ?? ""}
                onChange={formik.handleChange}
                error={
                  (formik.touched as any)["address.street_address"] &&
                  (formik.errors as any)["address.street_address"]
                }
                size="md"
                styles={{
                  label: {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    marginBottom: 8,
                  },
                  input: {
                    height: 40,
                    fontSize: "0.9rem",
                  },
                }}
              />
              <TextInput
                // withAsterisk
                name="address.city"
                label="City"
                placeholder="Enter City"
                value={formik.values.address?.city ?? ""}
                onChange={formik.handleChange}
                error={
                  (formik.touched as any)["address.city"] &&
                  (formik.errors as any)["address.city"]
                }
                size="md"
                styles={{
                  label: {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    marginBottom: 8,
                  },
                  input: {
                    height: 40,
                    fontSize: "0.9rem",
                  },
                }}
              />
              <TextInput
                // withAsterisk
                name="address.state"
                label="State"
                placeholder="Enter State"
                value={formik.values.address?.state ?? ""}
                onChange={formik.handleChange}
                error={
                  (formik.touched as any)["address.state"] &&
                  (formik.errors as any)["address.state"]
                }
                size="md"
                styles={{
                  label: {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    marginBottom: 8,
                  },
                  input: {
                    height: 40,
                    fontSize: "0.9rem",
                  },
                }}
              />
              <TextInput
                // withAsterisk
                name="address.zip_code"
                label="Zip Code"
                placeholder="Enter Zip Code"
                value={formik.values.address?.zip_code ?? ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                  formik.setFieldValue("address", {
                    ...formik.values.address,
                    zip_code: value,
                  });
                }}
                onBlur={formik.handleBlur}
                maxLength={5}
                error={
                  (formik.touched as any)["address.zip_code"] &&
                  (formik.errors as any)["address.zip_code"]
                }
                size="md"
                styles={{
                  label: {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    marginBottom: 8,
                  },
                  input: {
                    height: 40,
                    fontSize: "0.9rem",
                  },
                }}
              />
              <TextInput
                // withAsterisk
                name="address.country"
                label="Country"
                placeholder="Enter Country"
                value={formik.values?.address?.country ?? ""}
                onChange={formik.handleChange}
                error={
                  (formik.touched as any)["address.country"] &&
                  (formik.errors as any)["address.country"]
                }
                size="md"
                styles={{
                  label: {
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    marginBottom: 8,
                  },
                  input: {
                    height: 40,
                    fontSize: "0.9rem",
                  },
                }}
              />
            </SimpleGrid>
            <Stack gap="sm" mt="xl">
              <Checkbox
                label={
                  <Text size="sm" c="light.5">
                    I have read and agree to the{" "}
                    <Anchor
                      c="#124B8C"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenTermsModal(true);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      Terms and Condition
                    </Anchor>
                  </Text>
                }
                checked={termsAccepted}
                onChange={(event) => {
                  if (!termsAccepted) {
                    event.preventDefault();
                    setOpenTermsModal(true);
                  } else {
                    setTermsAccepted(event.currentTarget.checked);
                  }
                }}
                onClick={() => {
                  if (!termsAccepted) {
                    setOpenTermsModal(true);
                  }
                }}
                styles={{
                  label: {
                    fontSize: "0.875rem",
                  },
                }}
              />
              <Checkbox
                label={
                  <Text size="sm" c="light.5">
                    I have reviewed and consent to the{" "}
                    <Anchor
                      c="#124B8C"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenPrivacyModal(true);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      Privacy Policy
                    </Anchor>
                  </Text>
                }
                checked={privacyAccepted}
                onChange={(event) => {
                  if (!privacyAccepted) {
                    event.preventDefault();
                    setOpenPrivacyModal(true);
                  } else {
                    setPrivacyAccepted(event.currentTarget.checked);
                  }
                }}
                onClick={() => {
                  if (!privacyAccepted) {
                    setOpenPrivacyModal(true);
                  }
                }}
                styles={{
                  label: {
                    fontSize: "0.875rem",
                  },
                }}
              />
              <Checkbox
                label={
                  <Text size="sm" c="light.5">
                    I accept the{" "}
                    <Anchor
                      c="#124B8C"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenEulaModal(true);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      End-User License Agreement
                    </Anchor>
                  </Text>
                }
                checked={eulaAccepted}
                onChange={(event) => {
                  if (!eulaAccepted) {
                    event.preventDefault();
                    setOpenEulaModal(true);
                  } else {
                    setEulaAccepted(event.currentTarget.checked);
                  }
                }}
                onClick={() => {
                  if (!eulaAccepted) {
                    setOpenEulaModal(true);
                  }
                }}
                styles={{
                  label: {
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Stack>
          </>
        )}

        <Button
          fullWidth
          mt={{ base: "lg", sm: "xl" }}
          size="md"
          disabled={
            activeStep === 1 &&
            (!termsAccepted || !privacyAccepted || !eulaAccepted)
          }
          onClick={async () => {
            if (activeStep === 0) {
              const errors = await formik.validateForm();
              if (Object.keys(errors).length === 0) {
                setActiveStep(1);
              } else {
                formik.setTouched({
                  email: true,
                  password: true,
                  first_name: true,
                  last_name: true,
                  date_of_birth: true,
                });
              }
            } else {
              if (!termsAccepted || !privacyAccepted || !eulaAccepted) {
                showToast({
                  type: "error",
                  message:
                    "Please accept all terms and conditions to continue.",
                });
                return;
              }
              formik.handleSubmit();
            }
          }}
          styles={{
            root: {
              backgroundColor:
                activeStep === 1 &&
                termsAccepted &&
                privacyAccepted &&
                eulaAccepted
                  ? "#124B8C"
                  : activeStep === 0
                  ? "#124B8C"
                  : undefined,
              "&:hover": {
                backgroundColor:
                  activeStep === 1 &&
                  termsAccepted &&
                  privacyAccepted &&
                  eulaAccepted
                    ? "#0f3a6f"
                    : activeStep === 0
                    ? "#0f3a6f"
                    : undefined,
              },
              "&:disabled": {
                backgroundColor: theme.colors.neutral[2],
                color: theme.colors.neutral[6],
              },
            },
          }}
        >
          {activeStep === 1 ? "Sign Up" : "Next"}
        </Button>

        <Text mt={{ base: "md", sm: "lg" }} size="md" c="dimmed" ta="center">
          Already have an account ?
          <Text
            component="span"
            c="#124B8C"
            style={{ cursor: "pointer" }}
            fw={"bold.5"}
            ml={"xs"}
            onClick={() => {
              navigate("/auth/login");
            }}
          >
            Sign In
          </Text>
        </Text>
      </form>

      <DocumentModal
        opened={openEulaModal}
        onClose={() => setOpenEulaModal(false)}
        title="End-User License Agreement"
        content={<EULAContent />}
        onAgree={() => {
          setEulaAccepted(true);
          setOpenEulaModal(false);
        }}
      />

      <DocumentModal
        opened={openPrivacyModal}
        onClose={() => setOpenPrivacyModal(false)}
        title="Privacy Policy"
        content={<PrivacyPolicyContent />}
        onAgree={() => {
          setPrivacyAccepted(true);
          setOpenPrivacyModal(false);
        }}
      />

      <DocumentModal
        opened={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        title="Terms and Conditions"
        content={<TermsAndConditionsContent />}
        onAgree={() => {
          setTermsAccepted(true);
          setOpenTermsModal(false);
        }}
      />
    </Box>
  );
}
